import { AnimatedContentLoader, Button, GoalStepper } from '@components'
import { getContactPageUrl, getProductPageUrl, getSettingsPageUrl } from '@constants'
import { useBusinessContext, useFeature } from '@context'
import { fetchBusiness, updateBusiness } from '@query'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaForward } from 'react-icons/fa'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

export const InvoicingOnboarding: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { enabled: kraviaEnabled } = useFeature('kravia')

  const { data, isLoading, refetch } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    ...{
      refetchOnMount: true
    }
  })

  const { mutateAsync: skipOnboarding, isPending: isSkipping } = useMutation({
    mutationFn: () =>
      updateBusiness(
        { slug },
        {
          onboarding_updated_invoicing_settings: true,
          onboarding_created_invoicing_contact: true,
          onboarding_created_invoicing_product: true,
          onboarding_registered_to_kravia: true
        }
      ),
    ...{
      onSuccess: () => refetch()
    }
  })

  return (
    <Wrapper>
      <AnimatedContentLoader isLoading={isLoading}>
        <>
          <GoalStepper
            header={t('invoicing.steps.header')}
            goals={[
              {
                content: (
                  <Trans i18nKey="invoicing.steps.addInvoicingSettings">
                    <Link to={getSettingsPageUrl(slug, { page: 'invoicing-general' })} />
                  </Trans>
                ),
                isCompleted: data?.onboarding_updated_invoicing_settings
              },
              {
                content: (
                  <Trans i18nKey="invoicing.steps.addContactAndProduct">
                    <Link to={getContactPageUrl(slug)} />
                    <Link to={getProductPageUrl(slug)} />
                  </Trans>
                ),
                isCompleted:
                  data?.onboarding_created_invoicing_contact &&
                  data?.onboarding_created_invoicing_product,
                isUnderway:
                  data?.onboarding_created_invoicing_contact ||
                  data?.onboarding_created_invoicing_product
              },
              ...(kraviaEnabled
                ? [
                    {
                      content: (
                        <Trans i18nKey="invoicing.steps.registerToKravia">
                          <Link
                            to={getSettingsPageUrl(slug, {
                              page: 'invoicing-debt-collection'
                            })}
                          />
                        </Trans>
                      ),
                      isCompleted: data?.onboarding_registered_to_kravia
                    }
                  ]
                : [])
            ]}
          />

          <AdditionalContent>
            <Button
              icon={<FaForward />}
              onClick={() => skipOnboarding()}
              disabled={isSkipping}
              showSpinner={isSkipping}
            >
              {t('invoicing.steps.skipButton')}
            </Button>
          </AdditionalContent>
        </>
      </AnimatedContentLoader>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  display: flex;
  align-items: center;

  padding: ${({ theme }) => theme.spacing.sm}rem;
`

const AdditionalContent = styled.div`
  margin: ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
